import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  getAllCoupons,
  createCoupon,
  updateCoupon,
  deleteCoupon,
  getCouponById,
  getUsedCoupons,
  assignCoupon,
  getAllCouponsSummary,
} from "../../apis/coupons"; // Make sure these API functions exist and are correct
import { Coupon } from "./types/couponTypes";
import { toast } from "react-toastify";

interface CouponState {
  couponsSummary: any;
  coupons: any;
  usedCoupons: any;
  selectedCoupon: Coupon | null;
  loading: boolean;
  error: string | null;
  creating: boolean;
  updating: boolean;
  assigningCoupon: boolean;
  deleting: boolean;
}

const initialState: CouponState = {
  couponsSummary: null,
  coupons: null,
  usedCoupons: null,
  selectedCoupon: null,
  loading: false,
  error: null,
  creating: false,
  assigningCoupon: false,
  updating: false,
  deleting: false,
};

// Async Thunks
export const fetchAllCouponsSummary = createAsyncThunk(
  "coupon/fetchAllCouponsSummary",
  async (_, { rejectWithValue }) => {
    try {
      const res = await getAllCouponsSummary();
      return res?.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch coupons summary"
      );
    }
  }
);

export const fetchAllCoupons = createAsyncThunk(
  "coupon/fetchAllCoupons",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await getAllCoupons(payload);
      return res;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch coupons"
      );
    }
  }
);

export const fetchAllUsedCoupons = createAsyncThunk(
  "coupon/fetchAllUsedCoupons",
  async (payload: any, { rejectWithValue }) => {
    try {
      const res = await getUsedCoupons(payload);
      return res;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch coupons"
      );
    }
  }
);

export const createNewCoupon = createAsyncThunk(
  "coupon/createNewCoupon",
  async (payload: Partial<Coupon>, { rejectWithValue }) => {
    try {
      const res = await createCoupon(payload);
      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to create coupon"
      );
    }
  }
);

export const editCoupon = createAsyncThunk(
  "coupon/editCoupon",
  async (
    { couponId, payload }: { couponId: string; payload: Partial<Coupon> },
    { rejectWithValue }
  ) => {
    try {
      const res = await updateCoupon(couponId, payload);
      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to update coupon"
      );
    }
  }
);

export const deleteCouponAsync = createAsyncThunk(
  "coupon/deleteCoupon",
  async (couponId: string, { rejectWithValue }) => {
    try {
      await deleteCoupon(couponId);
      return couponId;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to delete coupon"
      );
    }
  }
);
export const assignCouponToUsers = createAsyncThunk(
  "coupon/assignCoupon",
  async (
    { couponId, users }: { couponId: string; users: string[] },
    { rejectWithValue }
  ) => {
    try {
      await assignCoupon(couponId, users);
      return couponId;
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to assign coupon");
      return rejectWithValue(
        error?.response?.data?.message || "Failed to assign coupon"
      );
    }
  }
);

export const fetchSingleCoupon = createAsyncThunk(
  "coupon/fetchSingleCoupon",
  async (couponId: string, { rejectWithValue }) => {
    try {
      const res = await getCouponById(couponId);
      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch coupon"
      );
    }
  }
);

// Slice
const couponSlice = createSlice({
  name: "coupon",
  initialState,
  reducers: {
    resetCouponState: (state) => {
      state.coupons = null;
      state.selectedCoupon = null;
      state.loading = false;
      state.creating = false;
      state.updating = false;
      state.deleting = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Summary
      .addCase(fetchAllCouponsSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllCouponsSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.couponsSummary = action.payload;
      })
      .addCase(
        fetchAllCouponsSummary.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )
      // Fetch All
      .addCase(fetchAllCoupons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllCoupons.fulfilled, (state, action) => {
        state.loading = false;
        state.coupons = action.payload;
      })
      .addCase(
        fetchAllCoupons.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )

      // Fetch Used Coupons
      .addCase(fetchAllUsedCoupons.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUsedCoupons.fulfilled, (state, action) => {
        state.loading = false;
        state.usedCoupons = action.payload;
      })
      .addCase(
        fetchAllUsedCoupons.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      )

      // Create
      .addCase(createNewCoupon.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(
        createNewCoupon.fulfilled,
        (state, action: PayloadAction<Coupon>) => {
          state.creating = false;
        }
      )
      .addCase(
        createNewCoupon.rejected,
        (state, action: PayloadAction<any>) => {
          state.creating = false;
          state.error = action.payload;
        }
      )

      // Update
      .addCase(editCoupon.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(editCoupon.fulfilled, (state, action: PayloadAction<Coupon>) => {
        state.updating = false;
      })
      .addCase(editCoupon.rejected, (state, action: PayloadAction<any>) => {
        state.updating = false;
        state.error = action.payload;
      })

      // Delete
      .addCase(deleteCouponAsync.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteCouponAsync.fulfilled, (state) => {
        state.deleting = false;
      })
      .addCase(
        deleteCouponAsync.rejected,
        (state, action: PayloadAction<any>) => {
          state.deleting = false;
          state.error = action.payload;
        }
      )
      // assign
      .addCase(assignCouponToUsers.pending, (state) => {
        state.assigningCoupon = true;
        state.error = null;
      })
      .addCase(assignCouponToUsers.fulfilled, (state) => {
        state.assigningCoupon = false;
      })
      .addCase(
        assignCouponToUsers.rejected,
        (state, action: PayloadAction<any>) => {
          state.assigningCoupon = false;
          state.error = action.payload;
        }
      )

      // Fetch One
      .addCase(fetchSingleCoupon.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchSingleCoupon.fulfilled,
        (state, action: PayloadAction<Coupon>) => {
          state.loading = false;
          state.selectedCoupon = action.payload;
        }
      )
      .addCase(
        fetchSingleCoupon.rejected,
        (state, action: PayloadAction<any>) => {
          state.loading = false;
          state.error = action.payload;
        }
      );
  },
});

export const { resetCouponState } = couponSlice.actions;
export default couponSlice.reducer;
